import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { SandboxService } from 'src/app/core/services/data/sandbox.service';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  IInvoiceNotificationDetailsResponse,
  IInvoicePrelimRecipientNotificationDetailsResponse,
} from 'src/app/shared/interfaces';
import { MatSnackBar, MatSnackBarModule, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-finalize-mi',
  standalone: true,
  imports: [MatProgressBarModule, MatSnackBarModule],
  templateUrl: './finalize-mi.component.html',
  styleUrl: './finalize-mi.component.scss',
})
export class FinalizeMiComponent implements OnInit {
  invoiceNumber: string = '';
  lotNumber: string = '';
  pdfPreviewUrl: SafeResourceUrl | undefined;
  currentEntityId: string = '';
  isInvoiceFinalized: boolean = false;
  isFinalzing: boolean = false;
  isCapableOfSendingEmail: boolean = false;
  customerEmailAddress: string = '';
  isNotificationSent: boolean = false;
  deliverySettings: IInvoicePrelimRecipientNotificationDetailsResponse | null = null;

  _route = inject(ActivatedRoute);
  _sandboxService = inject(SandboxService);
  _destroyRefService = inject(DestroyRef);
  _sanitizer = inject(DomSanitizer);
  _appGeneralState = inject(AppGeneralStateService);
  _dataService = inject(DataService);
  _snackBar = inject(MatSnackBar);

  ngOnInit(): void {
    this.invoiceNumber = this._route.snapshot.params['invoicenumber'];
    this.currentEntityId = this._route.snapshot.params['entityId'];
    this.lotNumber = this._route.snapshot.params['lotNumber'];
    this.onDisplayInvoice(this.invoiceNumber);
    this.getRecipientNotificationDeliverySettings(this.invoiceNumber); // This determines if we can finalize the invoice
  }

  onDisplayInvoice(invoiceNumber: string) {
    if (invoiceNumber.startsWith('GB')) {
      this._dataService.GetGbInvoicePdf(invoiceNumber, this.lotNumber).subscribe((response) => {
        const blob = new Blob([response.body!], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        this.pdfPreviewUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url);
      });
      return;
    }

    this._dataService.GetMiInvoicePdf(invoiceNumber, this.lotNumber, this.currentEntityId).subscribe((response) => {
      const blob = new Blob([response.body!], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      this.pdfPreviewUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url);
    });
  }

  onFinalizeInvoice() {
    const snackRef = this.openProcessingSnackBar('Finalizing Invoice...');
    this.finalizeInvoiceHttp(this.invoiceNumber, this.lotNumber, snackRef);
  }

  finalizeInvoiceHttp(invoiceNumber: string, lotNumber: string, snackRef?: MatSnackBarRef<TextOnlySnackBar>): void {
    this.isFinalzing = true;
    this._dataService
      .mi_FinalizePrelimInvoice(invoiceNumber, lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {
          snackRef?.dismiss();
          this.isFinalzing = false;
          console.error('error on finalize');
        },
        complete: () => {
          snackRef?.dismiss();
          this.isFinalzing = false;
          this.isInvoiceFinalized = true;
          this.onDisplayInvoice(invoiceNumber);
        },
      });
  }

  getRecipientNotificationDeliverySettings(invoiceNumber: string): void {
    this._dataService
      .getPrelimInvoiceDetailsForRecipientNotificationEmail(invoiceNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.deliverySettings = res;
          console.log('delivery settings', this.deliverySettings);
        },
        error: (err: any) => {
          this.isCapableOfSendingEmail = false;
        },
        complete: () => {
          if (
            this.deliverySettings != null &&
            this.deliverySettings.recipientEmailAddress !== '' &&
            this.deliverySettings.deliveryMethod.toLowerCase() === 'email'
          ) {
            this.isCapableOfSendingEmail = true;
          } else {
            this.isCapableOfSendingEmail = false;
          }
        },
      });
  }

  handlePdfDownload(blob: Blob) {
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = 'https://spacedownloadprod.blob.core.windows.net/downloadassets/MI01004052300006.pdf'; // Desired filename
    a.click();
    URL.revokeObjectURL(objectUrl); // Release the Blob URL
  }

  onSendInvoice(): void {
    const snackRef = this.openProcessingSnackBar('Sending Invoice to Customer via Email...');
    this._dataService
      .sendInvoiceNotificationEmail(this.invoiceNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {
          snackRef.dismiss();
          this.isFinalzing = false;
        },
        complete: () => {
          snackRef.dismiss();
          this.isFinalzing = false;
          this.isInvoiceFinalized = true;
          this.onUpdateInvoiceSendDate(this.invoiceNumber);
        },
      });
  }

  onUpdateInvoiceSendDate(invoiceNumber: string): void {
    const snackRef = this.openProcessingSnackBar('Saving that the Invoice has been emailed...');
    const dateSent = new Date();
    this._dataService
      .updateInvoiceNotificationEmailDateSent(this.invoiceNumber, dateSent.toISOString().split('T')[0])
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {
          snackRef.dismiss();
          this.isFinalzing = false;
        },
        complete: () => {
          snackRef.dismiss();
          this.isFinalzing = false;
          this.isNotificationSent = true;
          this.isInvoiceFinalized = true;
        },
      });
  }

  private openProcessingSnackBar(message: string): MatSnackBarRef<TextOnlySnackBar> {
    return this._snackBar.open(message, undefined, {
      duration: undefined,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['invoice-process-snackbar'],
    });
  }
}
